/**
 * Opportunity Types - E007-S007 (PRD-3682)
 * TypeScript interfaces for AI-detected optimization opportunities
 */

export type OpportunityType = 
  | 'pause_underperforming_creative'
  | 'reallocate_budget'
  | 'expand_audience'
  | 'optimize_timing';

export type ImpactType = 'savings' | 'revenue_increase' | 'roas_increase';

export type EffortLevel = 'low' | 'medium' | 'high';

export interface OpportunityImpact {
  type: ImpactType;
  amount: number;
  currency: string;
  period: 'per_day' | 'per_week' | 'per_month';
}

export interface AffectedItem {
  type: 'campaign' | 'creative' | 'ad_set';
  id: string;
  name: string;
  current_value?: Record<string, any>;
}

export interface Opportunity {
  id: string;
  organization_id: string;
  type: OpportunityType;
  title: string;
  description: string;
  impact: OpportunityImpact;
  effort: EffortLevel;
  confidence: number; // 0-1
  affected_items: AffectedItem[];
  implementation_steps?: string[];
  created_at: string;
  expires_at?: string;
  view_count?: number;
  click_count?: number;
}

export interface OpportunitiesResponse {
  opportunities: Opportunity[];
  metadata: {
    total: number;
    shown: number;
    last_updated: string | null;
  };
}

export interface ImplementResponse {
  success: boolean;
  implemented_at: string;
  affected_items: AffectedItem[];
  message: string;
}

export interface DismissRequest {
  reason: 'not_relevant' | 'already_done' | 'will_do_manually' | 'other';
  comment?: string;
}

export interface SnoozeRequest {
  duration_hours: number; // 1-168
}

// UI Helper Types

export interface OpportunityCardProps {
  opportunity: Opportunity;
  onViewDetails: (id: string) => void;
  onDismiss: (id: string) => void;
}

export interface OpportunityModalProps {
  opportunity: Opportunity | null;
  isOpen: boolean;
  onClose: () => void;
  onImplement: (id: string) => Promise<void>;
  onDismiss: (id: string, reason: string, comment?: string) => Promise<void>;
  onSnooze: (id: string, hours: number) => Promise<void>;
}

// Formatting helpers

export const opportunityTypeLabels: Record<OpportunityType, string> = {
  pause_underperforming_creative: 'Pause Underperforming',
  reallocate_budget: 'Reallocate Budget',
  expand_audience: 'Expand Audience',
  optimize_timing: 'Optimize Timing',
};

export const opportunityTypeIcons: Record<OpportunityType, string> = {
  pause_underperforming_creative: '⏸️',
  reallocate_budget: '💰',
  expand_audience: '🎯',
  optimize_timing: '⏰',
};

export const effortColors: Record<EffortLevel, string> = {
  low: 'text-green-600 bg-green-50',
  medium: 'text-yellow-600 bg-yellow-50',
  high: 'text-red-600 bg-red-50',
};

export const effortIcons: Record<EffortLevel, string> = {
  low: '🟢',
  medium: '🟡',
  high: '🔴',
};

export const impactTypeLabels: Record<ImpactType, string> = {
  savings: 'Save',
  revenue_increase: 'Increase Revenue',
  roas_increase: 'Increase ROAS',
};
